import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { CmsService, LanguageService, TranslatePipe, TranslationService } from '@spartacus/core';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { B2bunitService } from 'src/app/service/b2bunit/b2bunit.service';
import { ComponentService } from 'src/app/service/component.service';
import { MyAccountPermissionService } from 'src/app/service/myaccount/my-account-permission.service';
import { StorageService } from 'src/app/service/storage/storage.service';

@Component({
  selector: 'app-myaccount-permissions',
  templateUrl: './myaccount-permissions.component.html',
  styleUrls: ['./myaccount-permissions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [MatCheckbox, MatProgressSpinner, AsyncPipe, TranslatePipe],
})
export class MyaccountPermissionsComponent implements OnInit, OnDestroy {
  currentPageSubscription$: Subscription;
  b2bUserSubscription$: Subscription;
  permissionList$: Observable<any>;
  isAddEmployeePage: boolean = false;
  currentUserIsAdmin: boolean;
  hasPermissionUpdated: boolean;
  accountStorefrontPermission: any;
  myAccountPermission: any = [];
  accountPermission: any = [];
  permissionCodes: any = [];
  defaultValues: any = [];
  accountUser: string;
  activeLanguageSubscription$: Subscription;
  resetAddEmployeeFormSubscription$: Subscription;
  @ViewChildren('permissionCheckbox') permissionCheckboxes: QueryList<MatCheckbox>;

  @Output() activePermission = new EventEmitter<any>();
  @Output() permissionsChanged = new EventEmitter<any>();

  // preset values for new account
  setAccountPermission: any = ['PLACE_ORDERS', 'VIEW_WHOLESALE_PRICE', 'VIEW_ORDER_HISTORY'];
  setDisabledPermission: any = ['CREATE_EDIT_DELETE_USERS'];

  constructor(
    protected myAccountPermissionService: MyAccountPermissionService,
    protected cmsService: CmsService,
    protected b2bunitService: B2bunitService,
    protected translationService: TranslationService,
    protected cdr: ChangeDetectorRef,
    private componentService: ComponentService,
    private languageService: LanguageService,
    private storageService: StorageService
  ) {}

  ngOnInit(): void {
    // get account permission in session.storage
    this.currentPageSubscription$ = this.cmsService
      .getCurrentPage()
      .pipe(take(1))
      .subscribe((currentPage) => {
        if (currentPage && currentPage.pageId.includes('accountEditEmployeePage')) {
          let accountPermission = this.storageService.getSessionStorageItem('storeFrontPermissions');

          this.accountStorefrontPermission = accountPermission.split(',');
          this.permissionCodes = this.accountStorefrontPermission;

          this.savePermissionSelected(this.accountStorefrontPermission);
          this.cdr.detectChanges();
        }

        if (currentPage && currentPage.pageId.includes('accountAddEmployeePage')) {
          this.isAddEmployeePage = true;
          this.permissionCodes = this.setAccountPermission;
          this.savePermissionSelected(this.setAccountPermission);
          this.cdr.detectChanges();
        }
      });

    // check User if Admin
    this.b2bUserSubscription$ = this.componentService.isUserAdmin$.subscribe((admin) => {
      this.currentUserIsAdmin = admin ? true : false;
      this.cdr.markForCheck();
    });

    this.activeLanguageSubscription$ = this.languageService.getActive().subscribe(() => {
      this.permissionList$ = this.getMyPermissionList();
      this.cdr.detectChanges();
    });

    this.resetAddEmployeeFormSubscription$ = this.componentService.resetAddEmployeeForm$.subscribe((reset) => {
      if (reset) {
        this.reset();
      }
    });
  }

  getMyPermissionList(): Observable<any> {
    return this.myAccountPermissionService.getMyAccountPermissionList();
  }

  savePermissionSelected(permissionCodes: any) {
    for (const key in permissionCodes) {
      if (key !== null || key !== '') {
        this.myAccountPermission.push(permissionCodes[key]);
        this.defaultValues.push(permissionCodes[key]);
      }
    }
    this.componentService.setAccountPermissions(this.myAccountPermission);
  }

  onChangeEventFunc(status: string, $event: any) {
    if ($event.checked) {
      this.myAccountPermission.push(status);
    } else {
      const index = this.myAccountPermission.indexOf(status);
      if (index > -1) {
        this.myAccountPermission.splice(index, 1);
      }
    }

    const containsAll = (arr1: any, arr2: any) => arr2.every((arr2Item: any) => arr1.includes(arr2Item));
    const sameMembers = (arr1: any, arr2: any) => containsAll(arr1, arr2) && containsAll(arr2, arr1);

    if (sameMembers(this.defaultValues, this.myAccountPermission)) {
      this.hasPermissionUpdated = false;
    } else {
      this.hasPermissionUpdated = true;
    }

    this.activePermission.emit(this.myAccountPermission);
    this.permissionsChanged.emit(this.hasPermissionUpdated);
    this.cdr.detectChanges();
  }

  compareValue(permission: any, selected: any, checkbox: any) {
    if (checkbox && !checkbox.disabled) {
      for (const key in permission) {
        if (key !== null) {
          for (const idx in selected) {
            if (idx !== null) {
              if (selected[idx] === permission[key]) {
                return true;
              }
            }
          }
        }
      }
    }
  }

  checkValue(permission: any) {
    if (this.isAddEmployeePage || this.currentUserIsAdmin === false) {
      for (const key in permission) {
        if (key !== null) {
          for (const indx in this.setDisabledPermission) {
            if (indx !== null) {
              if (this.setDisabledPermission[indx] === permission[key]) {
                return true;
              }
            }
          }
        }
      }
    }
  }

  getTranslation(permissionCode: string): Observable<string> {
    let code = permissionCode.toLocaleLowerCase();
    return this.translationService.translate(`myAccount.permissions.${code}`);
  }

  reset() {
    this.myAccountPermission = [];
    this.hasPermissionUpdated = false;
    this.savePermissionSelected(this.setAccountPermission);
    this.permissionCheckboxes.forEach((checkbox: any) => {
      if (!this.setAccountPermission.includes(checkbox.value.code)) {
        checkbox.checked = false;
      }
    });
    this.activePermission.emit(this.myAccountPermission);
    this.permissionsChanged.emit(this.hasPermissionUpdated);
  }

  ngOnDestroy() {
    this.b2bUserSubscription$?.unsubscribe();
    this.currentPageSubscription$?.unsubscribe();
    this.activeLanguageSubscription$?.unsubscribe();
    this.resetAddEmployeeFormSubscription$?.unsubscribe();
  }
}
